import { useSearch } from '@faststore/sdk'
import * as Sentry from '@sentry/gatsby'
import type { Facet, FacetValue } from 'src/components/search/FilterV2/types'
import IconButton from 'src/components/ui/IconButton'
import SlideOver from 'src/components/ui/SlideOver'
import { useModal } from 'src/sdk/ui/modal/Provider'
import Icon from 'src/components/ui/Icon'
import { useFilter } from 'src/components/search/FilterV2/useFilter'
import Facets from 'src/components/search/FilterV2/Facets'
import type { IStoreSelectedFacet } from '@generated/graphql'
import { useCallback, useEffect, useState } from 'react'
import Button from 'src/components/ui/Button'

import QuickFiltersV3 from './QuickFiltersV3'

type GalleryFiltersV3Props = {
  totalCount: number
  facetsv2: Facet[] | undefined
  setIsFilterOpen: React.Dispatch<React.SetStateAction<boolean>>
  isFilterOpen: boolean
}

const GalleryFiltersV3 = ({
  totalCount,
  facetsv2,
  isFilterOpen,
  setIsFilterOpen,
}: GalleryFiltersV3Props) => {
  const [fakeLoading, setFakeLoading] = useState(false)
  const {
    state: { selectedFacets, page },
    setFacet,
    setFacets,
    toggleFacet,
  } = useSearch()

  const { onModalClose } = useModal()
  const { facets, selected, expanded, dispatch } = useFilter(facetsv2)
  const blueProductFacetKey = facetsv2?.find(
    (facet) => facet.name?.toLowerCase() === 'blueproduct'
  )?.key

  const handleFilterClick = (facetValue: FacetValue) => {
    const facetsToApply = (
      facetValue as FacetValue & {
        facetValuesToApply?: Array<{ key: string; value: string }>
      }
    ).facetValuesToApply ?? [
      {
        key: facetValue.key,
        value: facetValue.value,
      },
    ]

    const targetFacetKeys = [
      ...new Set(facetsToApply.map((facet) => facet.key)),
    ]

    const facetsWithoutTargets = selectedFacets.filter(
      (facet) => !targetFacetKeys.includes(facet.key)
    )

    if (facetValue.selected) {
      setFacets(facetsWithoutTargets)

      return
    }

    setFacets([
      ...facetsWithoutTargets,
      ...facetsToApply.map((facet) => ({
        key: facet.key,
        value: facet.value,
      })),
    ])
  }

  function handleModalClose() {
    dispatch({
      type: 'selectFacets',
      payload: selectedFacets,
    })

    onModalClose()
  }

  function handleSetFacet(facet: IStoreSelectedFacet, unique = true) {
    setFakeLoading(true)
    setFacet(facet, unique)
  }

  function handleToggleFacet(facet: IStoreSelectedFacet) {
    setFakeLoading(true)
    Sentry.setContext('search_state', {
      page,
      pageType: typeof page,
      facetKeys: selectedFacets.map(({ key }) => key),
      queryKeys: [...new URLSearchParams(window.location.search).keys()],
    })
    toggleFacet(facet)
  }

  useEffect(() => {
    setFakeLoading(false)
  }, [facetsv2])

  const filterFacets = useCallback(() => {
    if (!facetsv2) {
      return []
    }

    return selected
      .map(
        (filter) =>
          facetsv2
            ?.find((facet) => facet.key === filter.key)
            ?.values.find((value) => value.value === filter.value) as FacetValue
      )
      .filter((item) => item?.key !== 'category-1' && item)
  }, [facetsv2, selected])

  const [filteredFacets, setFilteredFacets] = useState<FacetValue[]>(
    filterFacets()
  )

  useEffect(() => {
    setFilteredFacets(filterFacets())
  }, [filterFacets])

  return (
    <div className="w-full min-w-0">
      <QuickFiltersV3
        filteredFacets={filteredFacets?.length ?? 0}
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
        handleFilterClick={handleFilterClick}
        facetsv2={facetsv2}
      />

      {isFilterOpen && (
        <SlideOver
          isOpen={isFilterOpen}
          onDismiss={() => setIsFilterOpen(false)}
          size="full"
          direction="rightSide"
          className="filter-modal__content md:w-1/4"
        >
          <div
            className={`filter-modal__body ${
              filteredFacets.length > 0 ? 'has-filters' : ''
            }`}
          >
            <header className="filter-modal__header py-6">
              <h2 className="title-display flex justify-center items-center">
                <span className="text-[22px] text-restructure-primary font-semibold">
                  Filtros
                </span>
              </h2>
              <IconButton
                data-testid="filter-modal-button-close"
                classes="filter-modal__button"
                aria-label="Close Filters"
                icon={<Icon name="X" width={24} height={24} />}
                onClick={() => handleModalClose()}
              />
            </header>
            <Facets
              facetsv2={facets}
              testId="mobile-store-filter"
              indicesExpanded={expanded}
              onFacetChange={(facet, type) => {
                if (type === 'RANGE') {
                  handleSetFacet(facet, true)

                  return
                }

                if (type === 'BOOLEAN' && facet.key === blueProductFacetKey) {
                  setFakeLoading(true)
                  handleFilterClick(facet as FacetValue)

                  return
                }

                handleToggleFacet(facet)
              }}
            />
          </div>
          <footer className="filter-modal__footer flex flex-col gap-5">
            <Button
              variant="primary"
              data-testid="filter-modal-button-apply"
              className="w-full bg-restructure-focus-primary text-restructure-primary-inverted text-lg font-semibold"
              onClick={() => {
                setFacets(selected)
                onModalClose()
              }}
            >
              <div className="flex justify-center items-center h-[65px]">
                {fakeLoading ? (
                  <img
                    src="https://decathlonstoreapp.vtexassets.com/arquivos/loader.gif"
                    alt="Carregando"
                    className="max-h-10 mix-blend-color-dodge"
                  />
                ) : (
                  `Ver ${totalCount} resultados`
                )}
              </div>
            </Button>
          </footer>
        </SlideOver>
      )}
    </div>
  )
}

export default GalleryFiltersV3
